import type { SessionSummary, SessionSummaryPlayer } from '@perplexicon/api-client';

/**
 * Renders a completed session's result generically from platform-owned
 * fields only — `game_players.result` (`won` / `lost` / `drawn` /
 * `forfeited` / `abandoned`) and the participant list — never from
 * game-specific state (CLAUDE.md §2, §3). This is shared by the dashboard's
 * "recent completed games" section, the History page, and the game host's
 * own forfeit/abandon outcome banner, so the three surfaces can never
 * describe the same result differently.
 *
 * A forfeit loss reads as "Forfeited", not "Lost" (APP_SPEC §44's "results
 * persist" and docs/APP_PLAN.md §12 M6's "a forfeit loss must not read as
 * an ordinary loss"), because the server's own `result` value already
 * distinguishes them — this function only chooses words for each value,
 * never re-derives the outcome itself.
 */
const RESULT_HEADLINES: Record<string, string> = {
  won: 'Won',
  lost: 'Lost',
  drawn: 'Draw',
  forfeited: 'Forfeited',
  abandoned: 'Abandoned',
};

export function resultHeadline(result: string | null): string {
  if (!result) return 'Completed';
  return RESULT_HEADLINES[result] ?? result;
}

export interface SessionOutcomeText {
  headline: string;
  detail?: string;
}

interface OutcomeSession {
  status: string;
  // The narrower `SessionSummaryPlayer` shape (not `SessionPlayer`): this
  // function is shared by callers passing a full `GameSession.players`
  // (a superset, structurally assignable here) and a `SessionSummary`'s own
  // lighter one, and only ever reads fields both already carry.
  players: SessionSummaryPlayer[];
  summary?: SessionSummary['summary'];
}

/**
 * A fuller description of a completed session's outcome from the
 * perspective of `selfUserId`, including which opponent forfeited/won when
 * that is known — used by the game host's own banner and the History page,
 * both of which have more room than the dashboard's compact list row.
 * Returns `null` for anything not yet `completed` (nothing to describe
 * yet — the in-progress game view already covers that).
 */
export function describeSessionOutcome(
  session: OutcomeSession,
  selfUserId: string,
): SessionOutcomeText | null {
  if (session.status !== 'completed') return null;

  const self = session.players.find((p) => p.userId === selfUserId) ?? null;
  const opponents = session.players.filter((p) => p.userId !== selfUserId);
  const isSolo = session.players.length <= 1;
  const headline = resultHeadline(self?.result ?? null);

  if (isSolo) {
    if (self?.result === 'abandoned') {
      return { headline, detail: 'This game was abandoned before it finished.' };
    }
    return { headline, detail: session.summary?.headline };
  }

  const opponent = opponents[0] ?? null;
  const opponentName = opponent?.username ?? 'your opponent';

  if (self?.result === 'forfeited') {
    return { headline, detail: `You resigned. ${opponentName} won.` };
  }
  if (self?.result === 'won' && opponent?.result === 'forfeited') {
    return { headline, detail: `${opponentName} resigned.` };
  }
  if (self?.result === 'won') {
    return { headline, detail: `You won against ${opponentName}.` };
  }
  if (self?.result === 'lost') {
    return { headline, detail: `${opponentName} won.` };
  }
  if (self?.result === 'drawn') {
    return { headline, detail: `You and ${opponentName} drew.` };
  }
  return { headline, detail: session.summary?.headline };
}
